import React, { createContext, Reducer, useReducer } from 'react';
import { Snackbar } from '@material-ui/core';
import { ErrorAlert, InfoAlert, SuccessAlert } from '../Alert';

type AppToastProps = {
  children: React.ReactNode;
};

export type AppToastReducerProps = {
  open: boolean;
  message?: string;
  type?: 'success' | 'error' | 'info';
};

export type AppToastContextProps = {
  showSuccess: (msg: string) => void;
  showError: (msg: string) => void;
  showInfo: (msg: string) => void;
};

export const AppToastContext = createContext<AppToastContextProps>({
  showSuccess: () => {},
  showError: () => {},
  showInfo: () => {},
});

type ToastAction =
  | { type: 'show_success' | 'show_error' | 'show_info'; message: string }
  | { type: 'close' };

export const AppToast = ({ children }: AppToastProps) => {
  const [props, dispatch] = useReducer<
    Reducer<AppToastReducerProps, ToastAction>
  >(
    (state, action) => {
      switch (action.type) {
        case 'show_success':
          return {
            open: true,
            message: action.message,
            type: 'success',
          };
        case 'show_error':
          return {
            open: true,
            message: action.message,
            type: 'error',
          };
        case 'show_info':
          return {
            open: true,
            message: action.message,
            type: 'info',
          };
        case 'close':
          return {
            open: false,
          };
        default:
          return state;
      }
    },
    {
      open: false,
    },
  );

  const showSuccess = (msg: string) => {
    dispatch({ type: 'show_success', message: msg });
  };

  const showError = (msg: string) => {
    dispatch({ type: 'show_error', message: msg });
  };

  const showInfo = (msg: string) => {
    dispatch({ type: 'show_info', message: msg });
  };

  const close = () => {
    dispatch({ type: 'close' });
  };

  const { open, type, message } = props;

  return (
    <AppToastContext.Provider value={{ showSuccess, showError, showInfo }}>
      {children}
      <Snackbar
        open={open}
        anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
        autoHideDuration={6000}
        onClose={close}
      >
        <div>
          {type === 'success' && (
            <SuccessAlert onClose={close}>{message}</SuccessAlert>
          )}
          {type === 'error' && (
            <ErrorAlert onClose={close}>{message}</ErrorAlert>
          )}
          {type === 'info' && <InfoAlert onClose={close}>{message}</InfoAlert>}
        </div>
      </Snackbar>
    </AppToastContext.Provider>
  );
};

export default AppToast;
